import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { FiClock, FiMaximize2, FiMinimize2, FiPlus, FiSearch, FiX } from 'react-icons/fi'
import { HiSparkles } from 'react-icons/hi'
import { useAuthentication } from '../../customHooks/useAuthentication'
import { useAccess } from '../../access/AccessContext'
import usePlanModuleNav from '../../customHooks/usePlanModuleNav'
import { buildFeatureContext, partitionFeatures } from '../../search/featureCatalog'
import { answerCompanyQuestion, searchFeatures } from '../../search/searchFeatures'
import { askAssistant, assistantLocale, deleteAssistantAttachment, loadAssistantEnabled, loadConversation, loadConversationList, loadLatestConversation, pollAssistantMessage, uploadAssistantImage } from '../../assistant/assistantApi'
import AssistantComposer from '../../assistant/AssistantComposer'
import { startDictation, dictationSupported, readingSupported } from '../../assistant/assistantSpeech'
import AssistantThread, { uniqueMessages } from '../../assistant/AssistantThread'
import './feature-search.css'

const DRAFT_KEY = 't4-assistant-draft'

function readDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY)
    if (!raw) return ''
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed.text === 'string' ? parsed.text : ''
  } catch (error) {
    return ''
  }
}

function shortcutLabel() {
  if (typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '')) return '⌘K'
  return 'Ctrl K'
}

function textOf(bundle, locale) {
  if (String(locale || '').toLowerCase().startsWith('es')) return bundle.es
  return bundle.en
}

const SUGGESTION_ORDER = ['create-invoice', 'create-payment', 'create-estimate', 'pos', 'settings-account', 'create-product', 'create-customer']

const SUGGESTION_COPY = {
  'create-invoice': { id: 'assistant_suggest_invoice', defaultMessage: 'How do I create an invoice?' },
  'create-payment': { id: 'assistant_suggest_payment', defaultMessage: 'How do I record a payment?' },
  'create-estimate': { id: 'assistant_suggest_estimate', defaultMessage: 'How do I create an estimate?' },
  pos: { id: 'assistant_suggest_pos', defaultMessage: 'How do I open the point of sale?' },
  'settings-account': { id: 'assistant_suggest_settings', defaultMessage: 'How do I open company settings?' },
  'create-product': { id: 'assistant_suggest_product', defaultMessage: 'How do I create a product?' },
  'create-customer': { id: 'assistant_suggest_customer', defaultMessage: 'How do I create a customer?' },
}

const MIC_NOTES = {
  unsupported: { id: 'assistant_dictate_unsupported', defaultMessage: 'This browser cannot dictate. Type the question instead.' },
  denied: { id: 'assistant_dictate_denied', defaultMessage: 'Microphone permission was denied.' },
  failed: { id: 'assistant_dictate_failed', defaultMessage: 'Dictation stopped. You can type the question.' },
  invalid_image: { id: 'assistant_image_rejected', defaultMessage: 'That file could not be attached. Use a JPEG, PNG, or WebP under 5 MB.' },
  image_too_large: { id: 'assistant_image_rejected', defaultMessage: 'That file could not be attached. Use a JPEG, PNG, or WebP under 5 MB.' },
  image_limit: { id: 'assistant_image_limit', defaultMessage: 'You can attach up to 3 images.' },
  image_off: { id: 'assistant_image_off', defaultMessage: 'The assistant is off, so the image was not saved. The text answer uses this company\'s catalog.' },
  documents: { id: 'assistant_documents_unsupported', defaultMessage: 'Documents are not saved. Use Add image for a JPEG, PNG, or WebP.' },
}

export default function FeatureSearch() {
  const { formatMessage: trans, locale } = useIntl()
  const history = useHistory()
  const { userInfo } = useAuthentication()
  const { access } = useAccess()
  const { canAccessPlanModule } = usePlanModuleNav(userInfo)
  const [panel, setPanel] = useState(null)
  const [query, setQuery] = useState('')
  const [askText, setAskText] = useState(readDraft)
  const [images, setImages] = useState([])
  const [micNote, setMicNote] = useState('')
  const [listening, setListening] = useState(false)
  const [answer, setAnswer] = useState(null)
  const [remoteEnabled, setRemoteEnabled] = useState(false)
  const [busy, setBusy] = useState(false)
  const [turns, setTurns] = useState([])
  const [remoteError, setRemoteError] = useState(null)
  const [conversationId, setConversationId] = useState(null)
  const [active, setActive] = useState(0)
  const [wide, setWide] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [conversations, setConversations] = useState([])
  const inputRef = useRef(null)
  const askRef = useRef(null)
  const busyRef = useRef(false)
  const attemptRef = useRef(null)
  const pollGen = useRef(0)
  const conversationRef = useRef(null)
  const freshRef = useRef(false)
  const bodyRef = useRef(null)
  const dictation = useRef(null)
  const listeningRef = useRef(false)
  const remoteRef = useRef(false)
  const pendingSettings = useRef(null)
  conversationRef.current = conversationId

  const partitioned = useMemo(() => {
    const ctx = buildFeatureContext({ userInfo, access, canAccessPlanModule })
    return partitionFeatures(ctx)
  }, [userInfo, access, canAccessPlanModule])

  const results = useMemo(() => searchFeatures(query, partitioned.available), [query, partitioned.available])
  const suggestions = useMemo(() => {
    const ids = new Set(partitioned.available.map((feature) => feature.id))
    return SUGGESTION_ORDER.filter((id) => ids.has(id)).slice(0, 3)
  }, [partitioned.available])

  useEffect(() => {
    setActive(0)
  }, [query, panel])

  useEffect(() => {
    try {
      if (askText) sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ text: askText }))
      else sessionStorage.removeItem(DRAFT_KEY)
    } catch (error) {
      /* the draft stays in memory */
    }
  }, [askText])

  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && String(event.key).toLowerCase() === 'k') {
        event.preventDefault()
        setPanel('search')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (panel !== 'ask') return undefined
    let stop = false
    const job = loadAssistantEnabled()
      .then((enabled) => {
        if (stop) return
        const on = enabled === true
        remoteRef.current = on
        setRemoteEnabled(on)
        if (on) setMicNote((note) => (note === 'image_off' ? '' : note))
      })
      .catch(() => {
        if (stop) return
        remoteRef.current = false
        setRemoteEnabled(false)
      })
    pendingSettings.current = job
    return () => {
      stop = true
    }
  }, [panel])

  useEffect(() => {
    if (panel !== 'ask' || remoteEnabled !== true || busyRef.current) return undefined
    let stop = false
    const gen = pollGen.current
    const recover = async () => {
      let currentId = conversationRef.current
      if (!currentId) {
        const latest = await loadLatestConversation()
        if (stop || pollGen.current !== gen) return
        if (!latest?.id) return
        currentId = latest.id
        conversationRef.current = currentId
        setConversationId(currentId)
      }
      const messages = await loadConversation(currentId)
      if (stop || pollGen.current !== gen) return
      if (messages === null) {
        conversationRef.current = null
        setConversationId(null)
        setTurns([])
        return
      }
      setTurns(uniqueMessages(messages))
      const last = [...messages].reverse().find((row) => row.role === 'assistant')
      if (!last || last.status === 'ready' || last.status === 'failed') return
      busyRef.current = true
      setBusy(true)
      pollAssistantMessage(last.id, { shouldStop: () => stop || pollGen.current !== gen }).then((result) => {
        if (pollGen.current !== gen) return
        busyRef.current = false
        setBusy(false)
        if (stop || result.stopped) return
        if (result.message) {
          setTurns((prev) => prev.map((row) => (row.id === result.message.id ? result.message : row)))
        }
        if (result.error) setRemoteError(result.error)
      })
    }
    recover().catch(() => {})
    return () => {
      stop = true
    }
  }, [panel, remoteEnabled])

  useEffect(() => {
    if (!panel) return undefined
    const id = window.setTimeout(() => {
      const node = panel === 'ask' ? askRef.current : inputRef.current
      node?.focus()
    }, 0)
    return () => window.clearTimeout(id)
  }, [panel])

  useEffect(() => {
    const node = bodyRef.current
    if (!node) return
    node.scrollTop = node.scrollHeight
  }, [turns, busy, answer, historyOpen, panel])

  const close = () => {
    pollGen.current += 1
    busyRef.current = false
    if (dictation.current) dictation.current.stop()
    dictation.current = null
    listeningRef.current = false
    setListening(false)
    setPanel(null)
    setQuery('')
    setAnswer(null)
    setTurns([])
    setRemoteError(null)
    setBusy(false)
    setActive(0)
    setHistoryOpen(false)
    setMicNote('')
  }

  const startNew = () => {
    pollGen.current += 1
    busyRef.current = false
    freshRef.current = true
    conversationRef.current = null
    setConversationId(null)
    setTurns([])
    setAnswer(null)
    setRemoteError(null)
    setBusy(false)
    setHistoryOpen(false)
    setMicNote('')
  }

  const showHistory = async () => {
    setHistoryOpen(true)
    const res = await loadConversationList()
    setConversations(res)
  }

  const openThread = async (id) => {
    freshRef.current = false
    conversationRef.current = id
    setConversationId(id)
    setHistoryOpen(false)
    setAnswer(null)
    setRemoteError(null)
    const messages = await loadConversation(id)
    if (Array.isArray(messages)) setTurns(uniqueMessages(messages))
  }

  const go = (feature) => {
    if (!feature?.path) return
    close()
    history.push(feature.path)
  }

  const onSearchKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((current) => (results.length ? (current + 1) % results.length : 0))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((current) => (results.length ? (current - 1 + results.length) % results.length : 0))
    } else if (event.key === 'Enter' && results[active]) {
      event.preventDefault()
      go(results[active])
    } else if (event.key === 'Escape') {
      event.preventDefault()
      close()
    }
  }

  const onAsk = async (event, preset) => {
    if (event && event.preventDefault) event.preventDefault()
    const text = String(preset || askText).trim()
    const waiting = images.some((image) => image.status === 'uploading')
    const failed = images.some((image) => image.status === 'error')
    if (!text || busyRef.current || waiting || failed) return
    const attachmentIds = images.map((image) => image.id).filter((id) => id)
    if (pendingSettings.current) {
      try { await pendingSettings.current } catch (error) { /* settings stays off */ }
    }
    if (!preset) setAskText('')
    const gen = pollGen.current
    busyRef.current = true
    setBusy(true)
    setAnswer(null)
    setRemoteError(null)
    const local = () => setAnswer(answerCompanyQuestion(text, { ...partitioned, locale }))
    const same = attemptRef.current
      && attemptRef.current.text === text
      && attemptRef.current.unfinished
      && attemptRef.current.messageId
    try {
      if (remoteRef.current !== true) {
        if (images.length) setMicNote('image_off')
        images.forEach((image) => {
          if (image.url) URL.revokeObjectURL(image.url)
        })
        setImages([])
        local()
        return
      }
      let threadId = same ? attemptRef.current.conversationId : conversationRef.current
      if (freshRef.current) {
        freshRef.current = false
        threadId = null
      } else if (!same && !threadId) {
        const latest = await loadLatestConversation()
        if (pollGen.current !== gen) return
        if (latest?.id) {
          threadId = latest.id
          conversationRef.current = latest.id
          setConversationId(latest.id)
        }
      }
      const pendingId = `pending-user-${gen}`
      const sentImages = images.filter((image) => image.id)
      setTurns((prev) => uniqueMessages(prev.concat([{
        id: pendingId,
        role: 'user',
        content: text,
        status: 'ready',
        attachments: sentImages.map((image) => ({ id: image.id, url: image.url })),
        created_at: new Date().toISOString(),
      }])))
      const result = await askAssistant({
        content: text,
        locale: assistantLocale(locale),
        conversationId: threadId,
        attachmentIds: same ? undefined : attachmentIds,
        idempotencyKey: same ? attemptRef.current.key : undefined,
        messageId: same ? attemptRef.current.messageId : undefined,
        shouldStop: () => pollGen.current !== gen,
      })
      if (result.conversationId) setConversationId(result.conversationId)
      if (result.messageId) {
        attemptRef.current = {
          text,
          key: result.idempotencyKey,
          messageId: result.messageId,
          conversationId: result.conversationId,
          unfinished: !result.message || result.message.status === 'queued' || result.message.status === 'processing',
        }
      }
      if (result.stopped || pollGen.current !== gen) return
      if (result.fallback) {
        setAskText(text)
        local()
        return
      }
      if (result.error) {
        setAskText(text)
        setRemoteError(result.error)
        return
      }
      if (result.message) {
        setImages([])
        setTurns((prev) => uniqueMessages(prev
          .filter((row) => row.id !== pendingId && row.id !== result.message.id)
          .concat([
            {
              id: `user-${result.message.id}`,
              role: 'user',
              content: text,
              status: 'ready',
              attachments: sentImages.map((image) => ({ id: image.id, url: image.url })),
              created_at: new Date().toISOString(),
            },
            result.message,
          ])))
      }
    } finally {
      if (pollGen.current === gen) {
        busyRef.current = false
        setBusy(false)
      }
    }
  }

  const addFiles = async (files) => {
    const room = Math.max(0, 3 - images.length)
    const chosen = files.slice(0, room)
    if (!chosen.length) {
      setMicNote('image_limit')
      return
    }
    if (files.length > room) setMicNote('image_limit')
    if (pendingSettings.current) {
      try { await pendingSettings.current } catch (error) { /* settings stays off */ }
    }
    if (remoteRef.current !== true) {
      setMicNote('image_off')
      return
    }
    setMicNote((note) => (note === 'image_off' ? '' : note))
    for (const file of chosen) {
      const key = `${Date.now()}-${Math.random()}`
      const url = URL.createObjectURL(file)
      setImages((prev) => prev.concat([{ key, url, status: 'uploading', removeLabel: trans({ id: 'assistant_remove_image', defaultMessage: 'Remove image' }) }]))
      try {
        const saved = await uploadAssistantImage(file)
        setImages((prev) => prev.map((image) => (image.key === key ? { ...image, id: saved.id, status: 'ready' } : image)))
      } catch (error) {
        setImages((prev) => prev.map((image) => (image.key === key ? { ...image, status: 'error' } : image)))
        setMicNote(error.code || 'invalid_image')
      }
    }
  }

  const removeImage = async (key) => {
    const image = images.find((item) => item.key === key)
    setImages((prev) => prev.filter((item) => item.key !== key))
    if (image && image.url) URL.revokeObjectURL(image.url)
    if (image && image.id) {
      try {
        await deleteAssistantAttachment(image.id)
      } catch (error) {
        /* the preview is already gone; the server still expires it */
      }
    }
  }

  const onMic = () => {
    if (listeningRef.current) {
      if (dictation.current) dictation.current.stop()
      listeningRef.current = false
      setListening(false)
      return
    }
    if (!dictationSupported()) {
      setMicNote('unsupported')
      return
    }
    setMicNote('')
    const prefix = askText.trim()
    listeningRef.current = true
    setListening(true)
    dictation.current = startDictation({
      lang: assistantLocale(locale),
      onText: (spoken) => setAskText(prefix ? `${prefix} ${spoken}` : spoken),
      onEnd: () => {
        listeningRef.current = false
        setListening(false)
      },
      onError: (code) => {
        listeningRef.current = false
        setListening(false)
        setMicNote(code)
      },
    })
  }

  const label = trans({ id: 'feature_search_label', defaultMessage: 'Search features' })
  const askLabel = trans({ id: 'feature_search_ai', defaultMessage: 'AI' })

  return (
    <>
      <div className="feature-search__bar">
        <button type="button" className="feature-search__trigger" onClick={() => setPanel('search')} aria-haspopup="dialog" aria-expanded={panel === 'search'}>
          <FiSearch aria-hidden="true" />
          <span className="feature-search__trigger-label">{label}</span>
          <kbd className="feature-search__kbd">{shortcutLabel()}</kbd>
        </button>
        <button type="button" className="feature-search__ai" onClick={() => setPanel('ask')} aria-haspopup="dialog" aria-expanded={panel === 'ask'} aria-label={askLabel}>
          <HiSparkles aria-hidden="true" />
          <span className="feature-search__ai-label">{askLabel}</span>
        </button>
      </div>
      {panel ? (
        <div className={panel === 'ask' ? 'feature-search__backdrop feature-search__backdrop--side' : 'feature-search__backdrop'} onMouseDown={close}>
          <div
            className={panel === 'ask' ? `feature-search__panel feature-search__drawer${wide ? ' is-wide' : ''}` : 'feature-search__panel'}
            role="dialog"
            aria-modal="true"
            aria-label={panel === 'ask' ? trans({ id: 'assistant_title', defaultMessage: 'AI assistant' }) : label}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {panel === 'search' ? (
              <>
                <input
                  ref={inputRef}
                  className="feature-search__input"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={onSearchKeyDown}
                  placeholder={trans({ id: 'feature_search_placeholder', defaultMessage: 'Invoices, products, warehouses…' })}
                  aria-label={label}
                />
                <ul className="feature-search__results">
                  {query.trim() && results.length === 0 ? (
                    <li className="feature-search__empty">{trans({ id: 'feature_search_empty', defaultMessage: 'No feature matches for this company.' })}</li>
                  ) : null}
                  {results.map((feature, index) => (
                    <li key={feature.id}>
                      <button
                        type="button"
                        className={index === active ? 'is-active' : ''}
                        onMouseEnter={() => setActive(index)}
                        onClick={() => go(feature)}
                      >
                        <span>{textOf(feature.label, locale)}</span>
                        <small>{textOf(feature.group, locale)}</small>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <form className="feature-search__ask" onSubmit={onAsk}>
                <header className="feature-search__drawer-head">
                  <div className="feature-search__ai-title">
                    <HiSparkles aria-hidden="true" />
                    <span>
                      <strong>{trans({ id: 'assistant_title', defaultMessage: 'AI assistant' })}</strong>
                      <small>{trans({ id: 'assistant_subtitle', defaultMessage: 'Answers use the features enabled for this company.' })}</small>
                    </span>
                  </div>
                  <div className="feature-search__drawer-tools">
                    <button type="button" aria-label={trans({ id: 'assistant_history', defaultMessage: 'History' })} onClick={showHistory}>
                      <FiClock aria-hidden="true" />
                    </button>
                    <button type="button" aria-label={trans({ id: 'assistant_new', defaultMessage: 'New chat' })} onClick={startNew}>
                      <FiPlus aria-hidden="true" />
                    </button>
                    <button type="button" aria-label={trans({ id: 'assistant_expand', defaultMessage: 'Resize' })} onClick={() => setWide((value) => !value)}>
                      {wide ? <FiMinimize2 aria-hidden="true" /> : <FiMaximize2 aria-hidden="true" />}
                    </button>
                    <button type="button" aria-label={trans({ id: 'assistant_close', defaultMessage: 'Close' })} onClick={close}>
                      <FiX aria-hidden="true" />
                    </button>
                  </div>
                </header>
                <div className="feature-search__drawer-body" ref={bodyRef}>
                  {historyOpen ? (
                    <div className="feature-search__history">
                      <p>{trans({ id: 'assistant_history', defaultMessage: 'History' })}</p>
                      {conversations.length === 0 ? (
                        <p className="feature-search__empty">{trans({ id: 'assistant_history_empty', defaultMessage: 'No earlier conversations.' })}</p>
                      ) : conversations.map((row) => (
                        <button type="button" key={row.id} className="feature-search__history-item" onClick={() => openThread(row.id)}>
                          {row.title || trans({ id: 'assistant_history', defaultMessage: 'History' })}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {!historyOpen && turns.length === 0 && !answer && !busy ? (
                    <div className="feature-search__welcome">
                      <p>{trans({ id: 'assistant_welcome', defaultMessage: 'Hello. I can explain how to use the features included in this company.' })}</p>
                      {suggestions.length > 0 ? (
                        <div className="feature-search__suggestions">
                          {suggestions.map((id) => {
                            const suggestion = trans(SUGGESTION_COPY[id])
                            return (
                              <button type="button" key={id} className="feature-search__chip" onClick={() => onAsk(null, suggestion)}>
                                {suggestion}
                              </button>
                            )
                          })}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {historyOpen ? null : (
                  <AssistantThread
                    pending={busy && remoteEnabled}
                    error={remoteError}
                    turns={turns}
                    available={partitioned.available}
                    onOpen={go}
                  />
                  )}
                  {!historyOpen && answer?.found && answer.available ? (
                    <div className="feature-search__answer">
                      <strong>{textOf(answer.feature.label, locale)}</strong>
                      <ol>
                        {answer.steps.map((step) => (
                          <li key={step}>{step}</li>
                        ))}
                      </ol>
                      <button type="button" className="feature-search__open" onClick={() => go(answer.feature)}>
                        {trans({ id: 'feature_search_open', defaultMessage: 'Open' })} {textOf(answer.feature.label, locale)}
                      </button>
                    </div>
                  ) : null}
                  {!historyOpen && answer?.found && !answer.available ? (
                    <p className="feature-search__empty">
                      {trans(
                        { id: 'feature_search_unavailable', defaultMessage: '{name} is not available for this company or your access.' },
                        { name: textOf(answer.feature.label, locale) },
                      )}
                    </p>
                  ) : null}
                  {!historyOpen && answer && !answer.found ? (
                    <p className="feature-search__empty">{trans({ id: 'feature_search_no_answer', defaultMessage: 'No matching feature for this company.' })}</p>
                  ) : null}
                </div>
                <AssistantComposer
                  ref={askRef}
                  value={askText}
                  onChange={setAskText}
                  disabled={busy || images.some((image) => image.status === 'uploading')}
                  placeholder={trans({ id: 'feature_search_ask_placeholder', defaultMessage: 'How do I create an invoice?' })}
                  label={trans({ id: 'feature_search_ask', defaultMessage: 'Ask' })}
                  images={images}
                  onFiles={addFiles}
                  onRemove={removeImage}
                  listening={listening}
                  onMic={onMic}
                  dictateLabel={trans({ id: 'assistant_dictate', defaultMessage: 'Dictate' })}
                  sendLabel={trans({ id: 'feature_search_ask', defaultMessage: 'Ask' })}
                  imageLabel={trans({ id: 'assistant_add_image', defaultMessage: 'Add image' })}
                  attachLabel={trans({ id: 'assistant_attach', defaultMessage: 'Attach' })}
                  onDocuments={() => setMicNote('documents')}
                />
                {micNote ? <p className="feature-search__composer-note" role="alert">{trans(MIC_NOTES[micNote] || MIC_NOTES.failed)}</p> : null}
                {dictationSupported() || readingSupported() ? (
                  <p className="feature-search__composer-privacy">
                    {trans({
                      id: 'assistant_voice_privacy',
                      defaultMessage: 'Dictation and read-aloud use this browser. Chrome and Edge may send the recording to the browser vendor. Tech4 does not receive the audio.',
                    })}
                  </p>
                ) : null}
                <p className="feature-search__scope">{trans({ id: 'feature_search_scope', defaultMessage: 'Answers stay inside the features enabled for this company.' })}</p>
              </form>
            )}
          </div>
        </div>
      ) : null}
    </>
  )
}
